import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore, { useAuth, useAuthRole } from '@/store/authStore';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRole?: 'customer' | 'seller' | 'admin';
    redirectUnauthenticated?: string;
    redirectUnauthorized?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requiredRole,
    redirectUnauthenticated = '/login',
    redirectUnauthorized = '/'
}) => {
    const { isAuthenticated, user, isLoading } = useAuth();
    const { hasRole } = useAuthRole();
    const location = useLocation();

    // Opcional: Verificar autenticación con el servidor al montar el componente
    useEffect(() => {
        if (isAuthenticated && !user) {
            // Intenta refrescar los datos del usuario si hay discrepancia
            useAuthStore.getState().refreshToken().catch(() => {
                useAuthStore.getState().logout();
            });
        }
    }, [isAuthenticated, user]);

    // Mostrar carga mientras se verifica la autenticación
    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">
            {/* Puedes personalizar este loader */}
            <span className="loading loading-spinner loading-lg"></span>
        </div>;
    }

    // Si no está autenticado, redirigir al login
    if (!isAuthenticated) {
        return (
            <Navigate
                to={redirectUnauthenticated}
                state={{ from: location }}
                replace
            />
        );
    }

    // Si requiere un rol específico y el usuario no lo tiene
    if (requiredRole && !hasRole(requiredRole)) {
        return (
            <Navigate
                to={redirectUnauthorized}
                state={{ from: location }}
                replace
            />
        );
    }

    return <>{children}</>;
};

export default ProtectedRoute;